import React, { useState, useEffect, useRef, useCallback } from 'react';
import { translate } from 'translations/i18n';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_COLLAB_REMOVE_PERMS_FLAG } from 'constants/featureFlags';
import { useIsCollaborating } from 'components/projects/collaboration/hooks';
import {
  ButtonCondensed,
  ButtonIconCondensed,
  Notice,
} from '@companycam/slab-web';

import {
  updateMetadata as _updateMetadata,
  convertActiveToken as _convertActiveToken,
  insertTokenStarter as _insertTokenStarter,
  transformTokens as _transformTokens,
} from './helpers/tokenutils';
import {
  splitGroupIntoUserTokens,
  removeSpaces,
  isTokenExactMatch,
  tokenFilterMatch,
} from './helpers/mentionTokenHelpers';

import TokenSuggestions from './TokenSuggestions';
import { TOKEN_TYPE_GROUP } from 'selectors/mentionTokens';
import RecordableTypes from 'constants/recordableTypes';

const StyledNotice = styled(Notice)`
  margin-bottom: var(--cc_size_spacing_s);

  .asset-gallery-content & {
    margin: var(--cc_size_spacing_s) var(--cc_size_spacing_l)
      calc(var(--cc_size_spacing_s) * -1);
  }
`;

const ALLOW_RESIZE_CHAR_LIMIT = 75;
const KEY_LEFT = 37;
const KEY_RIGHT = 39;
const KEY_ENTER = 13;

const CAPTURED_KEYS = [KEY_LEFT, KEY_RIGHT, KEY_ENTER];

// BEWARE: the existing token utils (tokenutils) are difficult to use with the current functionality direction
// Creating new, simpler helpers (see mentionTokenHelpers) instead using the older helpers is advisable

const TokenizedInput = ({
  tokenDefs,
  submitDisabled,
  onSubmit,
  placeholder,
  projectId,
  recordableType,
}) => {
  const [state, setState] = useState({
    value: '',
    metadata: [],
    activeToken: null,
    shouldUpdate: false,
  });
  const { value, metadata, activeToken, shouldUpdate } = state;
  const inputRef = useRef(null);
  const isCollaborating = useIsCollaborating(projectId);
  const flags = useFlags();
  const areCollabPermsHidden = flags[LD_COLLAB_REMOVE_PERMS_FLAG];

  // transformation: '@FirstLast' -> 'First Last' (actually just pulls from token defs by value)
  // returns { newtext, newtokens }
  const retokenizeForSubmit = useCallback(
    (_value, _metadata) => {
      return _transformTokens(_value, _metadata, (text, __value) => {
        let newTokenText = text;
        const matchingDefs = tokenDefs.filter(
          (def) => def.value === __value && def.type !== TOKEN_TYPE_GROUP,
        );
        if (matchingDefs.length > 0) {
          newTokenText = matchingDefs[0].text.trim();
        }

        return newTokenText;
      });
    },
    [tokenDefs],
  );

  // strips the 'TOKEN_STARTER' char from the front
  const getActiveTokenText = useCallback(() => {
    if (!activeToken) {
      return '';
    }

    return value.substring(
      activeToken.start + 1,
      activeToken.start + activeToken.len,
    );
  }, [activeToken, value]);

  // filters tokenDefs by active token text
  const filterTokens = useCallback(() => {
    if (!activeToken) {
      return [];
    }

    const filter = getActiveTokenText();
    return tokenDefs.filter((def) => tokenFilterMatch(def, filter));
  }, [activeToken, getActiveTokenText, tokenDefs]);

  // sets focus on input and sets specific cursor position
  function focusAtPosition(cursorPosition) {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.focus();
    inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
  }

  // convert the active token into a metadata token, and update focus/cursor
  const convertActiveToken = useCallback(
    (token) => {
      if (!activeToken) {
        return;
      }

      if (token.type === TOKEN_TYPE_GROUP) {
        const { updatedValue, updatedMetadata } = splitGroupIntoUserTokens(
          token,
          value,
          metadata,
        );

        setState({
          value: updatedValue,
          metadata: updatedMetadata,
        });

        focusAtPosition(updatedValue.length);

        return;
      }

      const {
        metadata: newMetadata,
        activeToken: newActiveToken,
        newtext,
        cursorPosition,
      } = _convertActiveToken(
        value,
        metadata,
        activeToken,
        removeSpaces(token.text),
        token.value,
        token.type,
      );

      setState({
        value: newtext,
        metadata: newMetadata,
        activeToken: newActiveToken,
      });
      focusAtPosition(cursorPosition);
    },
    [activeToken, metadata, value],
  );

  // automatically deactivate or convert the active token when updating
  const autoUpdateActiveToken = useCallback(() => {
    if (!shouldUpdate) return;
    setState({
      value,
      metadata,
      activeToken,
      shouldUpdate: false,
    });
    const filter = getActiveTokenText();
    const nameResults = filterTokens();

    // determine leading and trailing whitespace
    let filterEndsWithWhitespace = false;
    let filterBeginsWithWhitespace = false;

    if (filter.length > 0) {
      const filterFirstChar = filter[0];
      filterBeginsWithWhitespace = filterFirstChar !== filterFirstChar.trim();

      const filterLastChar = filter[filter.length - 1];
      filterEndsWithWhitespace = filterLastChar !== filterLastChar.trim();
    }

    // deactivate if filter begins with WS, or if no results and ends with WS
    const doCancelSuggestions =
      filterBeginsWithWhitespace ||
      (filterEndsWithWhitespace && nameResults.length === 0);

    if (doCancelSuggestions) {
      setState({ value, metadata, activeToken: null });
    }

    // convert automatically if exact (case-insensitive) match
    if (nameResults.length === 1) {
      const def = nameResults[0];
      if (isTokenExactMatch(def, filter)) {
        convertActiveToken(def);
      }
    }
  }, [
    activeToken,
    convertActiveToken,
    filterTokens,
    getActiveTokenText,
    metadata,
    shouldUpdate,
    value,
  ]);

  function onChange(event) {
    const newValue = event.target.value;
    const { metadata: newMetadata, activeToken: newActiveToken } =
      _updateMetadata(value, newValue, metadata, activeToken);

    setState({
      value: newValue,
      metadata: newMetadata,
      activeToken: newActiveToken,
      shouldUpdate: true,
    });
  }

  function insertTokenStarter() {
    if (!inputRef.current) {
      return;
    }

    const selstart = inputRef.current.selectionStart;
    const selend = inputRef.current.selectionEnd;

    const {
      metadata: newMetadata,
      activeToken: newActiveToken,
      newtext,
      cursorPosition,
    } = _insertTokenStarter(value, metadata, [selstart, selend]);

    setState({
      value: newtext,
      metadata: newMetadata,
      activeToken: newActiveToken,
    });
    focusAtPosition(cursorPosition);
  }

  const convertTopSuggestion = useCallback(() => {
    if (!activeToken) {
      return undefined;
    }

    const nameResults = filterTokens();
    if (nameResults.length > 0) {
      const def = nameResults[0];
      convertActiveToken(def);
      return true;
    }

    return false;
  }, [activeToken, convertActiveToken, filterTokens]);

  function reset() {
    setState({ value: '', metadata: [], activeToken: null });
  }

  const submitInput = useCallback(() => {
    const { newtext, newtokens } = retokenizeForSubmit(value, metadata);
    const preserveText = onSubmit(newtext, newtokens);
    if (!preserveText) {
      reset();
    }
  }, [metadata, onSubmit, retokenizeForSubmit, value]);

  useEffect(() => {
    const _keyDownListener = (event) => {
      const key = event.which;

      // on enter, possibly insert top suggestion; if done, don't newline
      if (key === KEY_ENTER) {
        if (event.ctrlKey || event.metaKey) {
          submitInput();
          event.preventDefault();
        } else {
          const didInsert = convertTopSuggestion();
          if (didInsert) {
            event.preventDefault();
          }
        }
      }

      // stop propagation for CAPTURED_KEYS
      if (CAPTURED_KEYS.includes(key)) {
        event.stopPropagation();
      }
    };
    const inputComponent = inputRef.current;
    inputComponent?.addEventListener('keydown', _keyDownListener);
    return () => {
      inputComponent?.removeEventListener('keydown', _keyDownListener);
    };
  }, [convertTopSuggestion, submitInput]);

  useEffect(() => {
    if (state.activeToken && state.shouldUpdate) {
      autoUpdateActiveToken();
    }
  }, [autoUpdateActiveToken, state]);

  function renderSuggestions() {
    if (!activeToken) {
      return null;
    }

    return (
      <TokenSuggestions
        tokenDefs={filterTokens()}
        onSelect={convertActiveToken}
        onCancel={() => {
          if (!activeToken) {
            return;
          }
          setState({ value, metadata, activeToken: null });
        }}
      />
    );
  }

  const onSubmitForm = (e) => {
    e?.preventDefault();
    if (submitDisabled) {
      return false;
    }
    return true;
  };

  return (
    <div className="comment-textarea-container">
      {isCollaborating ? (
        <StyledNotice size="small" iconName="domain">
          {areCollabPermsHidden
            ? translate('activity.tokenizedInput.alertMessage')
            : translate('activity.tokenizedInput.commentsSeenByCollabs')}
        </StyledNotice>
      ) : null}
      <form
        className={
          isCollaborating
            ? 'submit-container submit-container--withCollabSignage'
            : 'submit-container'
        }
        onSubmit={onSubmitForm}
      >
        <div className="input-container">
          <textarea
            className={
              value.length > ALLOW_RESIZE_CHAR_LIMIT ? 'allow-resize' : ''
            }
            data-testid={
              recordableType === RecordableTypes.PROJECT
                ? 'project__comments-text-area'
                : 'asset__comments-text-area'
            }
            ref={inputRef}
            placeholder={placeholder}
            spellCheck={false}
            rows="2"
            required
            value={value}
            onChange={(event) => onChange(event)}
          />
          <ButtonCondensed
            data-testid={
              recordableType === RecordableTypes.PROJECT
                ? 'project__comments-post-button'
                : 'asset__comments-post-button'
            }
            color="primary"
            disabled={submitDisabled}
            onClick={submitInput}
          >
            {translate('activity.tokenizedInput.post')}
          </ButtonCondensed>
          <div className="input-actions">
            <ButtonIconCondensed
              color="primary"
              size="small"
              iconName="at"
              onClick={(e) => {
                e?.preventDefault();
                insertTokenStarter();
              }}
              className="hint--left"
              aria-label={translate(
                'activity.tokenizedInput.mentionSomeoneAriaLabel',
              )}
              accessibilityLabel={translate(
                'activity.tokenizedInput.mentionSomeoneAriaLabel',
              )}
            />
          </div>
        </div>
        {renderSuggestions()}
      </form>
    </div>
  );
};

TokenizedInput.propTypes = {
  placeholder: PropTypes.string,
  projectId: PropTypes.string,
  tokenDefs: PropTypes.array,
  onSubmit: PropTypes.func,
  submitDisabled: PropTypes.bool,
  recordableType: PropTypes.oneOf([
    RecordableTypes.PHOTO,
    RecordableTypes.PROJECT,
    RecordableTypes.VIDEO,
  ]).isRequired,
};

export default TokenizedInput;
